/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import PatchManager from "App/utils/globe/chunk_lod/patch_manager";
import Visitor, {VisitorOrdering} from "App/utils/globe/chunk_lod/visitor";
import TreeNode from "App/utils/globe/chunk_lod/tree/node";
import {ChunkLODNodeState} from "App/utils/globe/chunk_lod/tree/chunk_lod_node";

/***
 * Take care about the tree structure, create and remove nodes when required
 * @param aPatch
 * @constructor
 */
export default
class ChunkLODTreeCreator extends Visitor {
    private _patch: PatchManager;
    private _view: any;
    private _nodesToCreate: { parent: TreeNode, childIndex: number }[];
    private _nodesToRemove: TreeNode[];

    constructor(aPatch: PatchManager) {
        super();

        this._patch = aPatch;

        this._ordering = VisitorOrdering.pre;
        this._visiteeConsumerFunction = this.manage.bind(this);

        this._view = null;

        this._nodesToCreate = [];
        this._nodesToRemove = [];
    }

    public setView(aView: any) {
        this._view = aView;
    }

    public manage(aVisitee: TreeNode) {

        let used: number = this._patch.getUsedNodes()[aVisitee.getAddress().flattenedTreeIndex];
        // check whether we should create the tree down to children or not
        aVisitee.setRequireChildren(aVisitee.getRequireDataForChildren(this._view) && used === 1);
        if (aVisitee.hasParent()) {
            // we have to inherit the requirement for data from parent as it can happen when we cut via view frustum
            // that some node decide to not require data for children, but its still existing child needs data for its children
            // (it happens, but I do do not yet think about the reason why, but some reason for it exists for sure, it is not a bug)
            aVisitee.setRequireChildren(aVisitee.getRequireChildren() && (aVisitee.getParent().getRequireChildren() || aVisitee.getParent().getParent() === null));
        }

        // check the status of the children
        if (aVisitee.getRequireChildren()) {
            let emptyChildrenIndices = aVisitee.getEmptyChildrenIndices();
            for (let i = 0; i < emptyChildrenIndices.length; i++) {
                this._nodesToCreate.push({
                    parent: aVisitee,
                    childIndex: emptyChildrenIndices[i]
                });
            }
        } else {
            let children = aVisitee.getChildren();
            for (let i = 0; i < children.length; i++) {
                if (children[i]) {
                    this._nodesToRemove.push(children[i]);
                }
            }
        }
    }

    /**
     * Perform the collected create requests
     */
    public update() {
        for (let i = 0; i < this._nodesToCreate.length; i++) {
            let request = this._nodesToCreate[i];
            let createdChild = request.parent.createChild(request.childIndex);
            let used: number = this._patch.getUsedNodes()[createdChild.getAddress().flattenedTreeIndex];
            createdChild.setIsUsed(used === 1);
            if (used === 0) {
                // set state of the node so that it is rendered, becauseu we do not have the data for it and we require that the levels are complete
                createdChild.setState(ChunkLODNodeState.SETTLEDONGPU);
            }
        }

        // remove the nodes from the tree
        for (let j = 0; j < this._nodesToRemove.length; j++) {
            this._nodesToRemove[j].getParent().removeChild(this._nodesToRemove[j]);
        }

        this._nodesToCreate = [];
        this._nodesToRemove = [];
    }

    public getNodesToRemove(): TreeNode[] {
        return this._nodesToRemove.slice();
    }
}
